// ── Domain model (snake_case — mirrors the API contract) ────────────────────────

export const EVENT_INBOX_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  DONE: 'DONE',
  FAILED: 'FAILED',
} as const;

export type EventInboxStatus = (typeof EVENT_INBOX_STATUS)[keyof typeof EVENT_INBOX_STATUS];

export interface EventInboxListItem {
  id: string;
  status: EventInboxStatus;
  message_id: string;
  exchange: string;
  attempts: number;
  last_error?: string | null;
  /** Structured rejection cause; null = structural failure (not a business rule). */
  failed_rule?: string | null;
  received_at: string;
  processed_at?: string | null;
}

export interface EventInboxDetail extends EventInboxListItem {
  vhost: string;
  payload: Record<string, unknown> | string;
}

export interface EventInboxStats {
  pending: number;
  processing: number;
  done: number;
  failed: number;
  total: number;
  /** FAILED rows grouped by `failed_rule` (null rule → key `unknown`). */
  by_failed_rule: Record<string, number>;
}

// ── Presentation metadata ───────────────────────────────────────────────────────

/**
 * Maps a status to its MUI semantic colour (used by both the Chip badge and the
 * KPI cards) and its i18n label key. Single source of truth so badge and stats bar
 * never drift.
 */
export const STATUS_META: Record<
  EventInboxStatus,
  { color: 'info' | 'warning' | 'success' | 'error'; labelKey: string }
> = {
  PENDING: { color: 'info', labelKey: 'status.PENDING' },
  PROCESSING: { color: 'warning', labelKey: 'status.PROCESSING' },
  DONE: { color: 'success', labelKey: 'status.DONE' },
  FAILED: { color: 'error', labelKey: 'status.FAILED' },
};

/** Fixed display order for the KPI cards and the filter chip group. */
export const STATUS_ORDER: EventInboxStatus[] = ['PENDING', 'PROCESSING', 'DONE', 'FAILED'];

// Query state (filters/sort/pagination) uses the shared `useTableState` +
// `FilterState<EventInboxListItem>` — no feature-specific filter/sort types.
// The list is always ordered `received_at desc` server-side (HOMCO-772).
